/**
 * The REAL third-party delivery client — Hasura-backed implementation of the
 * ThirdPartyDeliveryClient seam (replaces the mock):
 *
 * - quote():           partsDeliveryQuote Hasura action → services/parts fans out
 *                      to the live vendors (DoorDash sandbox today, Uber later)
 *                      and logs a parts_delivery_estimates batch.
 * - book():            insert_parts_deliveries_one (requested_vendor pins the
 *                      quoted vendor; the db-parts-deliveries trigger books the
 *                      courier) + links the shell run via parts_delivery_id.
 * - getStatusForRun(): polls the run's parts_delivery relationship.
 *
 * Uses the Apollo client instance directly (the seam is a plain object, not a
 * hook). All calls ride the signed-in user's JWT — authorization comes from the
 * dealer/dealer-admin permissions on parts_deliveries.
 */

import apolloClient from '../../../apollo/client';
import {
  GetRunPartsDeliveryDocument,
  type GetRunPartsDeliveryQuery,
  type GetRunPartsDeliveryQueryVariables,
} from '../queries/getRunPartsDelivery.query.generated';
import {
  InsertPartsDeliveryRequestDocument,
  LinkRunPartsDeliveryDocument,
  type InsertPartsDeliveryRequestMutation,
  type InsertPartsDeliveryRequestMutationVariables,
  type LinkRunPartsDeliveryMutation,
  type LinkRunPartsDeliveryMutationVariables,
} from '../queries/insertPartsDeliveryRequest.mutation.generated';
import {
  PartsDeliveryCancelDocument,
  type PartsDeliveryCancelMutation,
  type PartsDeliveryCancelMutationVariables,
} from '../queries/partsDeliveryCancel.mutation.generated';
import {
  PartsDeliveryQuoteDocument,
  type PartsDeliveryQuoteMutation,
  type PartsDeliveryQuoteMutationVariables,
} from '../queries/partsDeliveryQuote.mutation.generated';
import type { ThirdPartyDeliveryClient } from './client';
import type {
  TpBookRequest,
  TpDeliveryState,
  TpPoint,
  TpQuoteRequest,
  TpQuoteResult,
  TpVendor,
} from './types';

/** UI point → the action's PartsDeliveryPointInput (camelCase, flat address). */
const toPointInput = (p: TpPoint) => ({
  name: p.name,
  businessName: p.business_name ?? null,
  phone: p.phone ?? null,
  address: p.address,
  lat: p.lat ?? null,
  lng: p.lng ?? null,
  notes: p.notes ?? null,
});

/** UI point → the parts_deliveries pickup/dropoff jsonb (unified backend shape).
 * Only `formatted` is populated on the address struct — matches what the quote
 * action prices, and the DoorDash adapter flattens from `formatted` first. */
const toLocationJson = (p: TpPoint) => ({
  name: p.name,
  business_name: p.business_name ?? undefined,
  phone: p.phone ?? undefined,
  address: { street: '', city: '', state: '', postal_code: '', country: 'US', formatted: p.address },
  lat: p.lat ?? undefined,
  lng: p.lng ?? undefined,
  notes: p.notes ?? undefined,
  reference_tag: p.reference_tag ?? undefined,
});

export const hasuraClient: ThirdPartyDeliveryClient = {
  async quote(req: TpQuoteRequest): Promise<TpQuoteResult> {
    const { data } = await apolloClient.mutate<PartsDeliveryQuoteMutation, PartsDeliveryQuoteMutationVariables>({
      mutation: PartsDeliveryQuoteDocument,
      variables: {
        pickup: toPointInput(req.pickup),
        dropoff: toPointInput(req.dropoff),
        items: req.items.map(i => ({ name: i.name, quantity: i.quantity })),
        manifestTotalValueCents: req.manifest_total_value_cents ?? null,
        readyAt: req.ready_at ?? null,
      },
    });
    const out = data?.partsDeliveryQuote;
    return {
      quotes: (out?.quotes ?? []).map(q => ({
        vendor: q.vendor as TpVendor,
        quote_id: q.quoteId,
        fee_cents: q.feeCents,
        currency: q.currency,
        pickup_eta: q.pickupEta ?? undefined,
        dropoff_eta: q.dropoffEta ?? undefined,
        expires_at: q.expiresAt ?? undefined,
      })),
      errors: (out?.errors ?? []).map(e => ({ vendor: e.vendor as TpVendor, message: e.message })),
    };
  },

  async book(req: TpBookRequest): Promise<{ parts_delivery_id: string }> {
    const { data } = await apolloClient.mutate<
      InsertPartsDeliveryRequestMutation,
      InsertPartsDeliveryRequestMutationVariables
    >({
      mutation: InsertPartsDeliveryRequestDocument,
      variables: {
        object: {
          customer_id: req.customer_id,
          source: 'dealer-portal',
          created_by: req.created_by,
          requested_vendor: req.requested_vendor,
          pickup: toLocationJson(req.pickup),
          dropoff: { ...toLocationJson(req.dropoff), send_notifications: true },
          items: req.items,
          manifest_total_value_cents: req.manifest_total_value_cents ?? null,
          ...(req.verification ? { verification: req.verification } : null),
          ...(req.ready_at ? { pickup_window: { ready_at: req.ready_at } } : null),
        },
      },
    });
    const id = data?.insert_parts_deliveries_one?.id;
    if (!id) throw new Error('Failed to create the delivery request');

    // Link the shell run so list/detail read courier state through the relationship.
    await apolloClient.mutate<LinkRunPartsDeliveryMutation, LinkRunPartsDeliveryMutationVariables>({
      mutation: LinkRunPartsDeliveryDocument,
      variables: { runId: req.run_id, partsDeliveryId: id },
    });

    return { parts_delivery_id: String(id) };
  },

  async cancel(partsDeliveryId: number, reason?: string): Promise<{ delivery_status: string }> {
    try {
      const { data } = await apolloClient.mutate<PartsDeliveryCancelMutation, PartsDeliveryCancelMutationVariables>({
        mutation: PartsDeliveryCancelDocument,
        variables: { partsDeliveryId, reason: reason || null },
      });
      const out = data?.partsDeliveryCancel;
      if (!out) throw new Error('Cancel failed — no response from the delivery service');
      return { delivery_status: out.deliveryStatus };
    } catch (err: unknown) {
      // Surface the backend ActionError message verbatim ("try canceling again
      // in a minute", vendor rejections) instead of Apollo's wrapper prose.
      const apolloErr = err as { graphQLErrors?: { message: string }[]; message?: string };
      const message = apolloErr.graphQLErrors?.[0]?.message || apolloErr.message || 'Cancel failed';
      throw new Error(message);
    }
  },

  async getStatusForRun(runId: number): Promise<TpDeliveryState | null> {
    const { data } = await apolloClient.query<GetRunPartsDeliveryQuery, GetRunPartsDeliveryQueryVariables>({
      query: GetRunPartsDeliveryDocument,
      variables: { runId },
      fetchPolicy: 'network-only', // polling must bypass the cache
    });
    const pd = data?.mobility_runs_by_pk?.parts_delivery;
    if (!pd) return null;
    return {
      parts_delivery_id: String(pd.id),
      orchestration_status: pd.orchestration_status as TpDeliveryState['orchestration_status'],
      delivery_status: pd.delivery_status as TpDeliveryState['delivery_status'],
      vendor: (pd.vendor ?? 'doordash') as TpVendor,
      fee_cents: pd.fee_cents ?? undefined,
      currency: pd.currency ?? undefined,
      driver: (pd.driver as TpDeliveryState['driver']) ?? undefined,
      dropoff_eta: pd.dropoff_eta ?? undefined,
      tracking_url: pd.tracking_url ?? undefined,
      proof_of_delivery: (pd.proof_of_delivery as TpDeliveryState['proof_of_delivery']) ?? undefined,
      booked_at: pd.booked_at ?? undefined,
      completed_at: pd.completed_at ?? undefined,
      cancelation_reason: pd.cancelation_reason ?? undefined,
      last_error: (pd.last_error as TpDeliveryState['last_error']) ?? null,
      created_by: pd.created_by ?? undefined,
    };
  },
};
